import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AccordionItem, AppButton, Box, Checkbox, Inscription, Preloader, SearchInput, useClickOutside } from '@common-fe/common-fe';
import { capitalize } from 'lodash';

import { REQUIRED_TEXT } from '@/common/constants';
import regexp from '@/common/regexp';
import FakeSearchInput from '@/components/controls/FakeSearchInput/FakeSearchInput';
import { PlanTypesEnum } from '@/modules/plan/plan.types';
import currentTheme from '@/styles/theme';

import { AccordionItemWrapper, StyledItem } from '../../AddClaimProcessorAndTeamButton/NestedOrganizationSelectorLevel';
import { EllipsisText } from '../MultipleServicesSelector.styles';

import FakeInput from './FakeInput';
import useGetAllServicesQuery, { Service } from './useGetAllServices.query';

const MAX_SHOWN_SERVICES = 100;

interface Props {
  applyServicesIds: (value: string[]) => void;
  appliedServicesIds: string[];
  currentTeamId?: string;
  isSubmit?: boolean;
  isGenericClaimsTeam?: boolean;
  allServices?: Service[];
}

const MultipleServiceSelector: React.FC<Props> = ({
  appliedServicesIds,
  applyServicesIds,
  currentTeamId,
  isSubmit,
  isGenericClaimsTeam,
  allServices,
}) => {
  const ref = useRef(null);
  const [selectedServicesIds, setSelectedServicesIds] = useState<string[]>(appliedServicesIds);
  const [isShown, setIsShown] = useState(false);
  const [search, setSearch] = useState('');
  const { servicesGroupedByCategoryType, formattedData, isLoading, isSuccess } = useGetAllServicesQuery(search);
  const areAllServicesSelected = useMemo(() => Boolean(selectedServicesIds.length
    && selectedServicesIds.length === formattedData.length),
  [selectedServicesIds, formattedData]);
  const [isInit, setIsInit] = useState(true);
  useEffect(() => {
    if (isGenericClaimsTeam && selectedServicesIds.length === 0 && isSuccess && isInit) {
      const allIds = formattedData.map((item) => item.id);
      setSelectedServicesIds(allIds);
      applyServicesIds(allIds);
      setIsInit(!isInit);
    }
  }, [setSelectedServicesIds, applyServicesIds, isGenericClaimsTeam, selectedServicesIds, isSuccess, setIsInit, isInit, formattedData]);
  const appliedServicesNames = useMemo(() => formattedData
    ?.filter((service) => appliedServicesIds
      ?.some((id) => id === service.id))
    ?.map((service) => service?.name || ''),
  [appliedServicesIds, formattedData]);
  const getFakeSearchInput = useCallback(() => {
    if (!appliedServicesNames?.length) return '';
  
    if (appliedServicesNames.length > MAX_SHOWN_SERVICES) {
      return (
        <FakeInput
          items={appliedServicesNames}
          onClear={() => applyServicesIds([])}
          setIsShown={() => setIsShown(true)}
        />
      );
    }

    return appliedServicesNames.join(', ');
  }, [appliedServicesNames, applyServicesIds, setIsShown]);
  useClickOutside(ref, () => {
    setIsShown(false);
  });
  const isCategoryTypeChecked = useCallback((categoryType: PlanTypesEnum) => {
    const allCategoryTypeIds = servicesGroupedByCategoryType[categoryType]?.map((item) => item.id);
    const selectedCategoryTypeIds = selectedServicesIds
      .filter((selectedId) => allCategoryTypeIds.some((id) => id === selectedId));

    return Boolean(allCategoryTypeIds.length
      && selectedCategoryTypeIds.length === allCategoryTypeIds.length);
  }, [selectedServicesIds, servicesGroupedByCategoryType]);

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef && inputRef.current && isShown) {
      inputRef.current.focus();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [inputRef.current, isShown]);

  return (
    <Box ref={ref} key={currentTeamId || 'newTeamKey'}>
      <Box onFocus={() => setIsShown(true)}>
        <FakeSearchInput
          testId="MultipleServiceSelector_fakeSearch"
          isActive={isShown}
          placeholder="None"
          onClear={() => applyServicesIds([])}
          setIsShown={() => setIsShown(true)}
          {...isSubmit && !appliedServicesNames?.length ? {
            errorText: REQUIRED_TEXT,
          } : {}}
        >
          {appliedServicesIds.length === allServices?.length
            ? `All services (${allServices?.length})`
            : getFakeSearchInput()}
        </FakeSearchInput>
      </Box>
      <Box
        height="480px"
        width={{ min: '340px' }}
        background="canvas"
        justify="between"
        round="xs"
        pad={{ top: 'spacing12' }}
        style={{
          position: 'absolute',
          left: 'calc(100% + 2px)',
          boxShadow: currentTheme.shadows.active['box-shadow'],
          top: '-220px',
          ...(isShown ? {} : { display: 'none' }),
        }}
      >
        <Box>
          <Box
            pad={{ bottom: 'spacing12' }}
            border={{
              color: 'border1',
              side: 'bottom',
            }}
            height={{ min: 'fit-content' }}
          >
            <Box pad={{ horizontal: 'spacing16' }}>
              <SearchInput
                inputRef={inputRef}
                placeholder="Search"
                testId="service_selection"
                value={search}
                onChange={setSearch}
              />
            </Box>
          </Box>

          <Box margin={{ bottom: 'spacing8' }}>
            <Box style={{ overflowY: 'auto' }}>
              {search || isLoading ? null : (
                <Box pad={{ horizontal: 'spacing16' }} flex="grow">
                  <Box border={{ side: 'bottom', size: 'small', color: 'border1' }} pad={{ bottom: 'spacing8', top: 'spacing12' }}>
                    <Box direction="row" align="center">
                      <StyledItem
                        onClick={(e) => {
                          e.stopPropagation();
                          if (areAllServicesSelected) return setSelectedServicesIds([]);
                          setSelectedServicesIds(formattedData.map((item) => item.id));
                        }}
                        pad={{ horizontal: 'spacing8', vertical: 'spacing4' }}
                        margin={{ left: 'spacing2' }}
                        round="xxs"
                        direction="row"
                        width="285px"
                      >
                        <Checkbox
                          testId="all_services"
                          name="all-services"
                          checked={areAllServicesSelected}
                        />
                        <Inscription
                          lineHeight="20px"
                          color="textBody"
                        >
                          All services ({formattedData?.length || 0})
                        </Inscription>
                      </StyledItem>
                    </Box>
                  </Box>
                </Box>
              )}

              <AccordionItemWrapper flex="grow" pad={{ horizontal: 'spacing16', top: 'spacing8' }}>
                {isLoading ? <Preloader testId="MultipleServiceSelector"/> : ((Object.keys(servicesGroupedByCategoryType) as PlanTypesEnum[]).map((categoryType) => (
                  <AccordionItem
                    key={categoryType}
                    testId={categoryType}
                    text={(
                      <StyledItem
                        onClick={(e) => {
                          e.stopPropagation();
                          const allCategoryTypeIds = servicesGroupedByCategoryType[categoryType]?.map((item) => item.id);
                          if (isCategoryTypeChecked(categoryType)) {
                            return setSelectedServicesIds([
                              ...selectedServicesIds
                                .filter((id) => !allCategoryTypeIds
                                  .some((categoryServiceId) => categoryServiceId === id)),
                            ]);
                          }
                          setSelectedServicesIds([...new Set([...selectedServicesIds, ...allCategoryTypeIds])]);
                        }}
                        pad={{ horizontal: 'spacing8', vertical: 'spacing4' }}
                        margin={{ left: 'spacing2' }}
                        round="xxs"
                        direction="row"
                        width="285px"
                      >
                        <Checkbox
                          testId={categoryType}
                          name={categoryType}
                          checked={isCategoryTypeChecked(categoryType)}
                        />
                        <Inscription
                          color="textBody"
                          weight="normal"
                        >
                          {capitalize(categoryType.replace(regexp.DASH_SYMBOL, ' '))}
                        </Inscription>
                      </StyledItem>
                    )}
                    isLeftArrow
                    isExpandedDefault
                  >
                    <Box margin={{ left: '32px' }}>
                      {servicesGroupedByCategoryType[categoryType]?.map((serviceOfCategoryType) => (
                        <Box key={serviceOfCategoryType?.id} direction="row">
                          <StyledItem
                            onClick={(e) => {
                              e.stopPropagation();
                              if (selectedServicesIds.some((id) => id === serviceOfCategoryType.id)) {
                                return setSelectedServicesIds(selectedServicesIds
                                  .filter((id) => id !== serviceOfCategoryType.id));
                              }
                              setSelectedServicesIds([...selectedServicesIds, serviceOfCategoryType.id]);
                            }}
                            pad={{ horizontal: 'spacing8', vertical: 'spacing4' }}
                            margin={{ left: 'spacing2' }}
                            round="xxs"
                            direction="row"
                            width="285px"
                          >
                            <Box width={{ min: '36px' }}>
                              <Checkbox
                                testId={serviceOfCategoryType.id}
                                name={serviceOfCategoryType.id}
                                checked={selectedServicesIds.some((id) => id === serviceOfCategoryType.id)}
                                padding={false}
                              />
                            </Box>
                            <EllipsisText
                              color="textBody"
                              weight="normal"
                              title={serviceOfCategoryType?.name}
                            >
                              {serviceOfCategoryType?.name}
                            </EllipsisText>
                          </StyledItem>
                        </Box>
                      ))}
                    </Box>
                  </AccordionItem>
                )))}
              </AccordionItemWrapper>
            </Box>
          </Box>
        </Box>

        <Box height={{ min: 'fit-content' }}>
          <Box
            justify="between"
            align="center"
            direction="row"
            pad={{ horizontal: 'spacing16' }}
            height="70px"
            border={{
              color: 'border1',
              side: 'top',
            }}
          >
            <AppButton
              testId="cancel"
              buttonType="secondary"
              onClick={() => setIsShown(false)}
              width="145px"
            >
              Cancel
            </AppButton>
            <AppButton
              testId="apply"
              onClick={() => {
                applyServicesIds(selectedServicesIds);
                setIsShown(false);
              }}
              width="145px"
            >
              Apply
            </AppButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MultipleServiceSelector;
